import {
  faCalendarAlt,
  faCamera,
  faChartArea,
  faCheckCircle,
  faList,
  faMapMarkedAlt,
  faProjectDiagram,
  faSitemap,
  faSlidersH,
  faVideo,
  faVirus,
} from "@fortawesome/free-solid-svg-icons";
import {
  PermissionType,
  PermissionTypeValue,
} from "@/pages/Configuration/AdminConfiguration/Users/types/cognitoTypes";
import type { IconProps } from "@/shared/coralreef";
import type { AppIcons } from "../icon/Icon";
import { registerRoutePermission } from "./routePermissionRegistry";

export interface SidebarRoute {
  path: string;
  permission: PermissionTypeValue;
  text: string;
  collapsedText: string;
  testId: string;
  icon?: IconProps["icon"];
  svgIcon?: AppIcons;
  withSearch?: boolean;
  electronOnly?: boolean;
}

export type SidebarGroupKey = "track" | "explore" | "act";

export const DASHBOARD_ROUTE_PREFIX = "/tide/dashboard";

export const SIDEBAR_ROUTES: Record<SidebarGroupKey, SidebarRoute[]> = {
  track: [
    {
      path: "/tide/map",
      permission: PermissionType.Map,
      text: "Map",
      collapsedText: "Map",
      icon: faMapMarkedAlt,
      withSearch: true,
      testId: "sidebar-item-map",
    },
    {
      path: "/tide/goals",
      permission: PermissionType.Goals,
      text: "Goals",
      collapsedText: "Goals",
      icon: faCheckCircle,
      withSearch: true,
      testId: "sidebar-item-goals",
    },
    {
      path: "/tide/calendar",
      permission: PermissionType.Calendar,
      text: "Calendar",
      collapsedText: "Calendar",
      icon: faCalendarAlt,
      withSearch: true,
      testId: "sidebar-item-calendar",
    },
    {
      path: "/tide/biolife",
      permission: PermissionType.AlgaeDashboard,
      text: "Biolife",
      collapsedText: "Biolife",
      icon: faVirus,
      withSearch: true,
      testId: "sidebar-item-biolife",
    },
  ],
  explore: [
    {
      path: "/tide/analytics",
      permission: PermissionType.Data,
      text: "Analytics",
      collapsedText: "Data",
      icon: faChartArea,
      withSearch: true,
      testId: "sidebar-item-analytics",
    },
    {
      path: "/akva-analytics",
      permission: PermissionType.AkvaAnalytics,
      text: "Feeding Analytics",
      collapsedText: "Feeding Analytics",
      svgIcon: "fish",
      withSearch: true,
      testId: "akva-feeding-analytics-link",
    },
  ],
  act: [
    {
      path: "/marlin-v2/video",
      permission: PermissionType.Video,
      text: "Video",
      collapsedText: "Video",
      icon: faVideo,
      electronOnly: true,
      testId: "video-navigation-link",
    },
    {
      path: "/marlin-v2/feeding",
      permission: PermissionType.Feeding,
      text: "Feeding",
      collapsedText: "Feeding",
      icon: faList,
      electronOnly: true,
      testId: "feeding-navigation-link",
    },
    {
      path: "/marlin-v2/defaults",
      permission: PermissionType.Feeding,
      text: "Defaults",
      collapsedText: "Defaults",
      icon: faSlidersH,
      withSearch: true,
      testId: "sidebar-item-defaults",
    },
    {
      path: "/tide/feeding-strategies",
      permission: PermissionType.Feed,
      text: "Feed Automation",
      collapsedText: "Feed",
      icon: faProjectDiagram,
      withSearch: true,
      testId: "sidebar-item-feeding-strategies",
    },
    {
      path: "/tide/automation-form/select",
      permission: PermissionType.Automation,
      text: "Automation",
      collapsedText: "Automation",
      icon: faSitemap,
      withSearch: true,
      testId: "sidebar-item-automation",
    },
    {
      path: "/tide/camera-automation",
      permission: PermissionType.Camera,
      text: "Camera Automation",
      collapsedText: "Camera",
      icon: faCamera,
      withSearch: true,
      testId: "sidebar-item-camera-automation",
    },
  ],
};

export const SUBMENU_ROUTES = {
  integrations: {
    path: "/marlin-v2/admin-configuration/equipment/integration",
    permission: PermissionType.Support,
  },
  equipment: {
    path: "/marlin-v2/admin-configuration/equipment/configuration",
    permission: PermissionType.Support,
  },
  localServers: {
    path: "/marlin-v2/admin-configuration/local-servers",
    permission: PermissionType.Support,
  },
  dataRouter: {
    path: "/marlin-v2/admin-configuration/local-configuration",
    permission: PermissionType.Support,
  },
  notificationSettings: {
    path: "/marlin-v2/admin-configuration/notification-settings",
    permission: PermissionType.Support,
  },
  videoSettings: {
    path: "/marlin-v2/admin-configuration/video-settings",
    permission: PermissionType.Support,
  },
  feedingSettings: {
    path: "/marlin-v2/admin-configuration/feeding-settings",
    permission: PermissionType.Support,
  },
  adminSettings: {
    path: "/marlin-v2/admin-configuration/settings",
    permission: PermissionType.CameraCsv,
  },
  networkScanner: {
    path: "/akva-network-scanner",
    permission: PermissionType.CameraCsv,
  },
} satisfies Record<string, { path: string; permission: PermissionTypeValue }>;

Object.values(SIDEBAR_ROUTES)
  .flat()
  .forEach((r) => registerRoutePermission(r.path, r.permission));
Object.values(SUBMENU_ROUTES).forEach((r) =>
  registerRoutePermission(r.path, r.permission),
);
registerRoutePermission(DASHBOARD_ROUTE_PREFIX, PermissionType.Dashboard);
